/**
 * 워크스페이스·학년(클래스)당 선택 학급 — 전 페이지 공통.
 * 기기 localStorage + 같은 탭 커스텀 이벤트 (DB 없음).
 */

export const SELECTED_CLASS_GROUP_CHANGED_EVENT = 'cp-selected-class-group-changed';

export type SelectedClassGroupChangedDetail = {
  workspaceId: string;
  gradeId: string;
  groupId: string | null;
};

const SHARED_PREFIX = 'cp.selectedClassGroup.v1.';
const NOTE_GROUP_PREFIX = 'cp.noteSelectedGroup.v1.';
const DICE_LAST_GROUP_PREFIX = 'cp-slide-dice:lastGroup:';

function sharedKey(workspaceId: string, gradeId: string): string {
  return `${SHARED_PREFIX}${workspaceId}.${gradeId}`;
}

function noteGroupKey(scope: 'pointItem' | 'assignmentPoints', workspaceId: string, gradeId: string): string {
  return `${NOTE_GROUP_PREFIX}${scope}.${workspaceId}.${gradeId}`;
}

function diceKey(workspaceId: string, gradeId: string): string {
  return `${DICE_LAST_GROUP_PREFIX}${workspaceId}:${gradeId}`;
}

function readRaw(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const id = (window.localStorage.getItem(key) ?? '').trim();
    return id || null;
  } catch {
    return null;
  }
}

function writeRaw(key: string, groupId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    const id = (groupId ?? '').trim();
    if (!id) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, id);
  } catch {
    /* ignore quota */
  }
}

function emitChanged(workspaceId: string, gradeId: string, groupId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<SelectedClassGroupChangedDetail>(SELECTED_CLASS_GROUP_CHANGED_EVENT, {
        detail: { workspaceId, gradeId, groupId },
      })
    );
  } catch {
    /* ignore */
  }
}

/** 공유 키 → 레거시 노트 스코프 → 주사위 키 순으로 조회 */
export function loadSelectedClassGroupId(workspaceId: string, gradeId: string): string | null {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return null;

  const shared = readRaw(sharedKey(ws, gid));
  if (shared) return shared;

  for (const scope of ['pointItem', 'assignmentPoints'] as const) {
    const scoped = readRaw(noteGroupKey(scope, ws, gid));
    if (scoped) {
      writeRaw(sharedKey(ws, gid), scoped);
      return scoped;
    }
  }

  const dice = readRaw(diceKey(ws, gid));
  if (dice) {
    writeRaw(sharedKey(ws, gid), dice);
    return dice;
  }
  return null;
}

/**
 * 공유 키에 저장하고, 노트 양쪽 스코프·주사위 키에도 맞춰 쓴다.
 * keep-alive 페이지는 SELECTED_CLASS_GROUP_CHANGED_EVENT 로 동기화.
 */
export function saveSelectedClassGroupId(
  workspaceId: string,
  gradeId: string,
  groupId: string | null
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return;

  const next = (groupId ?? '').trim() || null;
  writeRaw(sharedKey(ws, gid), next);
  writeRaw(noteGroupKey('pointItem', ws, gid), next);
  writeRaw(noteGroupKey('assignmentPoints', ws, gid), next);
  writeRaw(diceKey(ws, gid), next);
  emitChanged(ws, gid, next);
}

export function subscribeSelectedClassGroupChanged(
  handler: (detail: SelectedClassGroupChangedDetail) => void
): () => void {
  if (typeof window === 'undefined') return () => {};
  const onEvent = (e: Event) => {
    const detail = (e as CustomEvent<SelectedClassGroupChangedDetail>).detail;
    if (!detail) return;
    handler(detail);
  };
  window.addEventListener(SELECTED_CLASS_GROUP_CHANGED_EVENT, onEvent);
  return () => window.removeEventListener(SELECTED_CLASS_GROUP_CHANGED_EVENT, onEvent);
}
